import { serializeEmbedParams } from "../utils/embed-params.js";
import { youtubeMediaDefaultProps } from "./props.js";
//#region src/dom/youtube/source.ts
/** Extract a YouTube video id from a raw 11-character id or any recognized URL. */
function parseYouTubeVideoId(src) {
	return parseYouTubeSource(src)?.id ?? null;
}
/**
* Parse a YouTube source string. Recognizes raw 11-character ids, `youtu.be` short links, `watch?v=`, `embed/`, `v/`,
* `shorts/` and `live/` URLs (with or without the `-nocookie` host), playlist URLs via the `list` parameter, and start
* times via the `t` parameter.
*/
function parseYouTubeSource(src) {
	if (!src) return null;
	if (/^[\w-]{11}$/.test(src)) return {
		id: src,
		kind: "video",
		listId: null,
		startTime: null,
		noCookie: false
	};
	const noCookie = src.includes("-nocookie");
	const videoMatch = VIDEO_MATCH_SRC.exec(src);
	const listMatch = PLAYLIST_MATCH_SRC.exec(src);
	const videoId = videoMatch?.[1] ?? null;
	const id = videoId === "videoseries" ? null : videoId;
	if (!id && !listMatch) return null;
	return {
		id,
		kind: id ? "video" : "playlist",
		listId: listMatch?.[1] ?? null,
		startTime: parseStartTime(src),
		noCookie
	};
}
/** Build the iframe `src` URL for an initial YouTube embed from the given props. */
function buildYouTubeIframeSrc(src, props = {}) {
	const parsed = parseYouTubeSource(src);
	if (!parsed) return "";
	const embedBase = parsed.noCookie ? EMBED_BASE_NOCOOKIE : EMBED_BASE;
	const params = {
		controls: props.controls === true ? null : 0,
		autoplay: props.autoplay,
		loop: props.loop,
		mute: props.defaultMuted,
		playsinline: props.playsInline ?? youtubeMediaDefaultProps.playsInline,
		preload: props.preload ?? youtubeMediaDefaultProps.preload,
		enablejsapi: 1,
		rel: 0,
		iv_load_policy: 3,
		start: parsed.startTime,
		...props.source?.engine?.youtube ?? void 0
	};
	if (parsed.kind === "playlist" && parsed.listId) return `${embedBase}?${serializeEmbedParams({
		listType: "playlist",
		list: parsed.listId,
		...params
	})}`;
	return `${embedBase}/${parsed.id}?${serializeEmbedParams(params)}`;
}
/**
* Parse the `t` parameter from a YouTube URL and convert it to seconds. Supports formats like: `t=171`, `t=171s`,
* `t=2m51s`, `t=2m`, `t=1h30m15s`.
*/
function parseStartTime(url) {
	const tValue = /[?&]t=([\dhms]+)/i.exec(url)?.[1]?.toLowerCase();
	if (!tValue) return null;
	let totalSeconds = 0;
	let hasValue = false;
	const hours = /(\d+)h/.exec(tValue)?.[1];
	if (hours) {
		totalSeconds += Number.parseInt(hours, 10) * 3600;
		hasValue = true;
	}
	const minutes = /(\d+)m/.exec(tValue)?.[1];
	if (minutes) {
		totalSeconds += Number.parseInt(minutes, 10) * 60;
		hasValue = true;
	}
	const seconds = /(\d+)s?$/.exec(tValue)?.[1];
	if (seconds) {
		totalSeconds += Number.parseInt(seconds, 10);
		hasValue = true;
	}
	return hasValue ? totalSeconds : null;
}
const EMBED_BASE = "https://www.youtube.com/embed";
const EMBED_BASE_NOCOOKIE = "https://www.youtube-nocookie.com/embed";
const VIDEO_MATCH_SRC = /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/|live\/))((?:\w|-){11})/;
const PLAYLIST_MATCH_SRC = /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/.*?[?&]list=)([\w-]+)/;
//#endregion
export { buildYouTubeIframeSrc, parseYouTubeSource, parseYouTubeVideoId };

//# sourceMappingURL=source.js.map